/**
 * Strategy Builder — DSS spec generator.
 *
 * Translates the friendly {@link StrategyModel} into a valid DSS v1.0 document.
 * Each condition contributes (a) any indicators it needs and (b) a boolean
 * expression node. Indicators are de-duplicated by a stable key so two
 * conditions sharing "SMA(50)" reference one indicator. Warmup is auto-sized to
 * the largest indicator lookback so the compiler never rejects for warmup.
 */

import type { Condition, RuleGroup, StrategyModel } from './model.js';

type Expr = unknown;

interface IndicatorDef {
  type: string;
  period?: number;
  [k: string]: unknown;
}

/** Accumulates unique indicators keyed by a canonical name. */
class IndicatorSet {
  private map = new Map<string, { name: string; def: IndicatorDef; lookback: number }>();

  /** Add an indicator, returning the reference name to use in expressions. */
  add(type: string, params: Record<string, number>, lookback: number): string {
    const paramKey = Object.entries(params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}${v}`)
      .join('_');
    const name = `${type.toLowerCase()}_${paramKey}`;
    if (!this.map.has(name)) {
      this.map.set(name, { name, def: { type, ...params }, lookback });
    }
    return name;
  }

  toRecord(): Record<string, IndicatorDef> {
    const out: Record<string, IndicatorDef> = {};
    for (const { name, def } of this.map.values()) out[name] = def;
    return out;
  }

  maxLookback(): number {
    let max = 0;
    for (const { lookback } of this.map.values()) if (lookback > max) max = lookback;
    return max;
  }
}

/** Translate one condition into a boolean DSS expression, registering indicators. */
function conditionToExpr(c: Condition, ind: IndicatorSet): Expr {
  switch (c.kind) {
    case 'price_above_ma': {
      const ref = ind.add(c.maType, { period: c.period }, c.period);
      return { '>': ['close', { ind: ref }] };
    }
    case 'price_below_ma': {
      const ref = ind.add(c.maType, { period: c.period }, c.period);
      return { '<': ['close', { ind: ref }] };
    }
    case 'ma_cross_up': {
      const fast = ind.add(c.maType, { period: c.fast }, c.fast);
      const slow = ind.add(c.maType, { period: c.slow }, c.slow);
      return { crosses_above: [{ ind: fast }, { ind: slow }] };
    }
    case 'ma_cross_down': {
      const fast = ind.add(c.maType, { period: c.fast }, c.fast);
      const slow = ind.add(c.maType, { period: c.slow }, c.slow);
      return { crosses_below: [{ ind: fast }, { ind: slow }] };
    }
    case 'rsi_below': {
      const ref = ind.add('RSI', { period: c.period }, c.period + 1);
      return { '<': [{ ind: ref }, c.level] };
    }
    case 'rsi_above': {
      const ref = ind.add('RSI', { period: c.period }, c.period + 1);
      return { '>': [{ ind: ref }, c.level] };
    }
    case 'breakout_high': {
      // Break above the prior N-bar high (exclude current so a bar can't break
      // its own high).
      const ref = ind.add('DONCHIAN_HIGH', { period: c.period }, c.period + 1);
      return { '>': ['close', { ind: ref }] };
    }
    case 'breakdown_low': {
      const ref = ind.add('DONCHIAN_LOW', { period: c.period }, c.period + 1);
      return { '<': ['close', { ind: ref }] };
    }
    case 'price_change_above': {
      const ref = ind.add('PCT_CHANGE', { period: c.period }, c.period + 1);
      // PCT_CHANGE is fractional; compare against pct/100.
      return { '>': [{ ind: ref }, c.pct / 100] };
    }
    case 'price_change_below': {
      const ref = ind.add('PCT_CHANGE', { period: c.period }, c.period + 1);
      return { '<': [{ ind: ref }, c.pct / 100] };
    }
  }
}

/** Combine a rule group's conditions into a single boolean expression. */
function groupToExpr(group: RuleGroup, ind: IndicatorSet): Expr | undefined {
  const parts = group.conditions.map((c) => conditionToExpr(c, ind));
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];
  return group.join === 'all' ? { all: parts } : { any: parts };
}

export interface BuildResult {
  spec: Record<string, unknown>;
  /** Non-fatal notes surfaced to the user (e.g. auto-adjusted warmup). */
  notes: string[];
}

/**
 * Build a DSS v1.0 document from the friendly model. Does not validate against
 * the engine — the backend compiler is the source of truth — but produces a
 * well-formed document that should compile for reasonable inputs.
 */
export function buildDssSpec(model: StrategyModel): BuildResult {
  const notes: string[] = [];
  const ind = new IndicatorSet();

  // For a long-and-short strategy the same entry/exit conditions apply to both
  // sides; for short-only we mirror the entry as the short entry.
  const wantLong = model.direction === 'long' || model.direction === 'both';
  const wantShort = model.direction === 'short' || model.direction === 'both';

  const entryExpr = groupToExpr(model.entry, ind);
  const exitExpr = groupToExpr(model.exit, ind);

  const sizing = { type: model.risk.sizingMode, value: model.risk.sizingValue };

  const sideRules = (side: 'long' | 'short'): Record<string, unknown> => {
    const rules: Record<string, unknown> = { sizing };
    if (entryExpr) rules.entry = entryExpr;
    if (exitExpr) rules.exit = exitExpr;
    if (model.risk.timeStopBars > 0) rules.time_stop_bars = model.risk.timeStopBars;
    // Percent stop/TP: expressed as price levels is not knowable pre-fill, so
    // we express them as rule exits via unrealized_pct against entry.
    const protectiveExits: unknown[] = [];
    if (model.risk.stopLossPct > 0) {
      // Long: exit if down stopLossPct; short: exit if up (adverse) stopLossPct.
      protectiveExits.push(
        side === 'long'
          ? { '<': [{ const: 'unrealized_pct' }, -model.risk.stopLossPct] }
          : { '<': [{ const: 'unrealized_pct' }, -model.risk.stopLossPct] },
      );
    }
    if (model.risk.takeProfitPct > 0) {
      protectiveExits.push({ '>': [{ const: 'unrealized_pct' }, model.risk.takeProfitPct] });
    }
    if (protectiveExits.length > 0) {
      const existing = rules.exit as unknown | undefined;
      const all = existing ? [existing, ...protectiveExits] : protectiveExits;
      rules.exit = all.length === 1 ? all[0] : { any: all };
    }
    return rules;
  };

  const rules: Record<string, unknown> = {};
  if (wantLong) rules.long = sideRules('long');
  if (wantShort) rules.short = sideRules('short');

  // Warmup: max indicator lookback, floored at 10.
  const warmup = Math.max(10, ind.maxLookback());

  const execution: Record<string, unknown> = {
    fill: 'next_bar_open',
    slippage: { type: 'pct', value: model.costsSlippagePct },
    fees: { type: 'pct', value: model.costsFeesPct },
  };
  if (wantShort) execution.allow_shorting = true;

  const test: Record<string, unknown> = {
    start: toIso(model.startDate),
    end: toIso(model.endDate),
    seed: model.seed,
  };

  // Optional Tier 3 rigor.
  if (model.rigor.walkForward) {
    test.walk_forward = {
      in_sample_months: model.rigor.walkForwardIsMonths,
      out_sample_months: model.rigor.walkForwardOosMonths,
      roll: true,
    };
  }
  if (model.rigor.monteCarlo) {
    // block_bootstrap resamples trades WITH REPLACEMENT, so the total-return
    // band is a genuine distribution. (trade_shuffle only reorders trades, so
    // total return is order-invariant and its band would collapse to a point.)
    test.monte_carlo = { method: 'block_bootstrap', runs: model.rigor.monteCarloRuns };
  }
  if (model.rigor.crisisPresets.length > 0) {
    test.crisis_presets = model.rigor.crisisPresets;
  }

  const spec: Record<string, unknown> = {
    dss_version: '1.0',
    name: model.name || 'Untitled Strategy',
    universe: { symbols: model.symbols, per_symbol_independent: true },
    data: { interval: model.interval, warmup_bars: warmup },
    indicators: ind.toRecord(),
    rules,
    execution,
    portfolio: { initial_cash: model.initialCash },
    test,
  };

  if (Object.keys(ind.toRecord()).length === 0) delete spec.indicators;

  if (model.risk.stopLossPct > 0 || model.risk.takeProfitPct > 0) {
    notes.push('Stop-loss / take-profit are applied as rule-based exits on unrealized P&L (evaluated each bar, filled next open).');
  }

  return { spec, notes };
}

/** Accept a plain YYYY-MM-DD (from a date input) and return an ISO timestamp. */
function toIso(date: string): string {
  if (!date) return date;
  // A bare date is treated as UTC midnight.
  return date.length === 10 ? `${date}T00:00:00.000Z` : date;
}
